/**
 * The property block at the top of the rail.
 *
 * Everyone sees which property they are working in — its logo and name, as
 * the first thing in the rail. Only a cross-property administrator can switch:
 * for them the block is a button that opens the property list, which makes
 * the current working scope visible (a super admin editing "the users" is
 * editing one hotel's users) and moves between properties without signing
 * out. For everyone else it is the home link the logo always was — to the
 * user's own home, not to the dashboard: a signage manager has no
 * documents:read and would have landed on a "you do not have access" page.
 */

import { useState, type ReactNode } from 'react'
import { Check, ChevronsUpDown } from 'lucide-react'
import { Link } from '@/app/router'
import { useAuth } from '@/lib/auth'
import { BrandMark } from '@/components/ui/primitives'
import { HotelLogo } from '@/components/domain/HotelLogo'
import { cn } from '@/lib/utils'

export function PropertySwitcher({ home, collapsed }: { home: string; collapsed: boolean }) {
  const { session, properties, selectProperty, resolvingProperty } = useAuth()
  const [open, setOpen] = useState(false)

  const isSuperAdmin = session?.permissions.includes('system:admin') ?? false
  const current = session?.property ?? null
  // A single property is not switchable, and neither is one nobody has picked
  // yet — the chooser screen handles that case.
  const switchable = isSuperAdmin && current !== null && properties.length > 1

  const logo = current?.logo_url ?? null
  // A hotel may go by its logo alone. The logo then says the name, and the
  // rail does not print EREG under it; without either, EREG it is.
  const name = current?.name || (logo ? null : 'EREG')
  // Hover titles and screen readers still need words for a nameless logo.
  const spoken = current?.name || current?.code || 'EREG'
  // A hotel's logo is usually a wordmark, and a 36px square cropped one to a
  // few letters of it. So a logo gets a line of its own above the name, drawn
  // whole; only the EREG mark, which is square, still sits beside it.
  const stacked = logo !== null && !collapsed

  const text = (
    <span className={cn('min-w-0 flex-1 text-start leading-tight', collapsed && 'sr-only')}>
      <span
        className={cn(
          'block truncate text-[14px] font-semibold text-[var(--rail-text-strong)]',
          !name && 'sr-only',
        )}
      >
        {name ?? spoken}
      </span>
      <span className="block truncate text-[12px] text-[var(--rail-muted)]">
        {switchable ? 'Switch property' : 'Guest registration'}
      </span>
    </span>
  )
  const chevron = switchable && !collapsed && (
    <ChevronsUpDown className="h-4 w-4 shrink-0 text-[var(--rail-muted)]" aria-hidden />
  )

  const identity: ReactNode = stacked ? (
    <>
      {/* A fixed height, so the name does not jump when the image arrives;
          object-contain keeps a very wide logo whole within the rail. */}
      <HotelLogo src={logo} surface="dark" className="h-10 w-auto max-w-full" />
      <span className="flex w-full items-center gap-3">
        {text}
        {chevron}
      </span>
    </>
  ) : (
    <>
      {logo ? (
        // Collapsed: the whole logo, small, rather than a cropped corner of it.
        <HotelLogo src={logo} surface="dark" className="h-9 w-full object-center rtl:object-center" />
      ) : (
        <BrandMark size={36} />
      )}
      {text}
      {chevron}
    </>
  )

  const block = cn(
    'flex w-full rounded-[12px] transition-colors hover:bg-[var(--rail-2)]',
    stacked ? 'flex-col items-start gap-2.5 p-2.5' : 'items-center gap-3 p-2',
    collapsed && 'justify-center',
    // The collapsed rail is 52px inside; give a wide logo all of it.
    collapsed && logo && 'px-0',
  )

  return (
    <div className="relative px-3 pt-4">
      {switchable ? (
        <button
          type="button"
          onClick={() => setOpen((value) => !value)}
          aria-expanded={open}
          aria-haspopup="menu"
          disabled={resolvingProperty}
          title={collapsed ? `${spoken} — switch property` : undefined}
          className={block}
        >
          {identity}
        </button>
      ) : (
        <Link to={home} className={block} title={collapsed ? spoken : undefined}>
          {identity}
        </Link>
      )}

      {open && switchable && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} aria-hidden />
          <div
            role="menu"
            className={cn(
              'fade-in absolute z-50 w-[17.5rem] rounded-[14px] border border-[var(--border-strong)] bg-[var(--surface)] p-2 text-[var(--text)] shadow-[var(--shadow-lg)]',
              collapsed ? 'start-[calc(100%-4px)] top-3' : 'start-3 top-full mt-1.5',
            )}
          >
            <div className="px-2 py-1 text-[11px] font-semibold uppercase tracking-[0.08em] text-[var(--text-muted)]">
              Switch property
            </div>
            <ul className="flex flex-col gap-0.5">
              {properties.map((property) => {
                const active = property.id === current.id
                return (
                  <li key={property.id}>
                    <button
                      role="menuitem"
                      disabled={resolvingProperty}
                      onClick={() => {
                        setOpen(false)
                        if (!active) void selectProperty(property.id)
                      }}
                      className={cn(
                        'flex w-full items-center gap-2 rounded-[10px] px-2 py-2 text-start text-[13px] transition-colors',
                        'disabled:cursor-not-allowed disabled:opacity-55',
                        active
                          ? 'bg-[var(--brand-subtle)] text-[var(--brand)]'
                          : 'hover:bg-[var(--surface-2)]',
                      )}
                    >
                      <span className="min-w-0 flex-1">
                        <span className="block truncate font-medium">
                          {property.name || property.code}
                        </span>
                        {property.name && (
                          <span className="block text-[11px] text-[var(--text-muted)]">
                            {property.code}
                          </span>
                        )}
                      </span>
                      {active && <Check className="h-3.5 w-3.5 shrink-0" aria-hidden />}
                    </button>
                  </li>
                )
              })}
            </ul>
          </div>
        </>
      )}
    </div>
  )
}
